import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import type { AgentSettingsVersion } from '../types'
import { useAgentSettingsLabels } from '../agentSettingsLabels'

export interface AgentVersionHistoryProps {
  versions: readonly AgentSettingsVersion[]
  selectedVersion: number | null
  onChange: (version: number) => void
}

type VersionValue = string | number | null | undefined

interface DisplayValue {
  marker: string
  value: string
}

const displayPolicyValue = (
  value: VersionValue,
  inheritedLabel: string,
  emptyLabel: string
): DisplayValue => {
  if (value === undefined) {
    return { marker: 'inherited', value: inheritedLabel }
  }
  if (value === null || value === '') {
    return { marker: 'empty', value: emptyLabel }
  }
  return { marker: String(value), value: String(value) }
}

const displayIdentityValue = (
  value: VersionValue,
  notApplicableLabel: string
): DisplayValue =>
  value === undefined || value === null || value === ''
    ? { marker: 'not-applicable', value: notApplicableLabel }
    : { marker: String(value), value: String(value) }

/** Immutable version inspection only; selection never hydrates the draft form. */
export function AgentVersionHistory({
  versions,
  selectedVersion,
  onChange,
}: AgentVersionHistoryProps) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()
  const selected = versions.find(
    (version) => version.version === selectedVersion
  )

  const policyValue = (value: VersionValue) =>
    displayPolicyValue(value, t('versions.inherited'), t('versions.notSet'))
  const identityValue = (value: VersionValue) =>
    displayIdentityValue(value, t('versions.notApplicable'))

  return (
    <section className="mt-6" aria-labelledby="version-history-title">
      <div id="version-history-title">
        <SectionLabel>{t('versions.title')}</SectionLabel>
      </div>
      {versions.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel body={t('versions.empty')} />
        </div>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('versions.version')}</TableHead>
                <TableHead>{t('versions.publisher')}</TableHead>
                <TableHead>{t('versions.publishedAt')}</TableHead>
                <TableHead>{t('versions.digest')}</TableHead>
                <TableHead>{t('versions.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => (
                <TableRow
                  key={version.version}
                  data-selected={
                    selectedVersion === version.version || undefined
                  }
                >
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => onChange(version.version)}
                      className="font-mono text-sm underline underline-offset-2 hover:no-underline"
                      aria-pressed={selectedVersion === version.version}
                    >
                      {version.version}
                    </button>
                  </TableCell>
                  <TableCell>
                    {version.publishedByName ?? version.publishedBy}
                  </TableCell>
                  <TableCell className="text-xs">
                    <time dateTime={new Date(version.publishedAt).toISOString()}>
                      {labels.dateTime(version.publishedAt) ??
                        t('versions.notSet')}
                    </time>
                  </TableCell>
                  <TableCell
                    className="max-w-[12rem] truncate font-mono text-xs"
                    title={version.payloadDigest}
                  >
                    {version.payloadDigest}
                  </TableCell>
                  <TableCell>
                    {version.publishReason ?? t('versions.noReason')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {selected !== undefined && (
        <section
          className="mt-4 rounded-lg border border-border bg-card p-4"
          aria-labelledby="selected-version-detail-title"
          data-testid="agent-version-detail"
          data-version={selected.version}
          data-scope={selected.scope}
          data-scope-key={identityValue(selected.scopeKey).marker}
          data-phase={selected.phase}
          data-agent-key={selected.agentKey}
          data-autonomy-rung={policyValue(selected.autonomyRung).marker}
          data-cost-per-run={
            policyValue(selected.costCeilingPerRunMinor).marker
          }
          data-cost-per-phase={
            policyValue(selected.costCeilingPerPhaseMinor).marker
          }
          data-currency={policyValue(selected.currency).marker}
          data-policy-preset={policyValue(selected.policyPresetKey).marker}
          data-named-human-merge={
            selected.reviewPolicy === undefined
              ? 'inherited'
              : selected.reviewPolicy.requiresNamedHumanMerge === true
                ? 'required'
                : 'empty'
          }
          data-second-reviewer-rule={
            selected.reviewPolicy === undefined
              ? 'inherited'
              : policyValue(selected.reviewPolicy.secondReviewerRule).marker
          }
          data-escalation-hours={
            selected.reviewPolicy === undefined
              ? 'inherited'
              : policyValue(selected.reviewPolicy.escalationHours).marker
          }
          data-output-permissions={
            selected.outputPermissions === undefined
              ? 'inherited'
              : selected.outputPermissions.length === 0
                ? 'none'
                : selected.outputPermissions.join('|')
          }
          data-client-override-reason={
            policyValue(selected.clientOverrideReason).marker
          }
          data-payload-digest={selected.payloadDigest}
          data-published-by={selected.publishedByName ?? selected.publishedBy}
          data-published-at={selected.publishedAt}
          data-publish-reason={
            selected.publishReason === undefined ||
            selected.publishReason === ''
              ? 'none'
              : selected.publishReason
          }
        >
          <h3
            id="selected-version-detail-title"
            className="text-sm font-semibold"
          >
            {t('versions.selectedDetail', { version: selected.version })}
          </h3>
          <dl className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.identity')}
              </dt>
              <dd className="mt-1 font-mono text-xs">
                {labels.scope(selected.scope)} ·{' '}
                {identityValue(selected.scopeKey).value} ·{' '}
                {t(`phases.${selected.phase}`)} · {selected.agentKey} ·{' '}
                {selected.version}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.publication')}
              </dt>
              <dd className="mt-1">
                {selected.publishedByName ?? selected.publishedBy} ·{' '}
                {labels.dateTime(selected.publishedAt) ?? t('versions.notSet')}{' '}
                ·{' '}
                {selected.publishReason ?? t('versions.noReason')}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.digest')}
              </dt>
              <dd className="mt-1 break-all font-mono text-xs">
                {selected.payloadDigest}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.autonomy')}
              </dt>
              <dd className="mt-1">
                {selected.autonomyRung === undefined
                  ? policyValue(selected.autonomyRung).value
                  : labels.rung(selected.autonomyRung)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.costPerRun')}
              </dt>
              <dd className="mt-1">
                {policyValue(selected.costCeilingPerRunMinor).value}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.costPerPhase')}
              </dt>
              <dd className="mt-1">
                {policyValue(selected.costCeilingPerPhaseMinor).value}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.currency')}
              </dt>
              <dd className="mt-1">{policyValue(selected.currency).value}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.policyPreset')}
              </dt>
              <dd className="mt-1">
                {policyValue(selected.policyPresetKey).value}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.namedHumanMerge')}
              </dt>
              <dd className="mt-1">
                {selected.reviewPolicy === undefined
                  ? t('versions.inherited')
                  : selected.reviewPolicy.requiresNamedHumanMerge === true
                    ? t('versions.required')
                    : t('versions.notSet')}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.secondReviewer')}
              </dt>
              <dd className="mt-1">
                {selected.reviewPolicy === undefined
                  ? t('versions.inherited')
                  : policyValue(selected.reviewPolicy.secondReviewerRule).value}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.escalation')}
              </dt>
              <dd className="mt-1">
                {selected.reviewPolicy === undefined
                  ? t('versions.inherited')
                  : policyValue(selected.reviewPolicy.escalationHours).value}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.outputPermissions')}
              </dt>
              <dd className="mt-1">
                {selected.outputPermissions === undefined
                  ? t('versions.inherited')
                  : selected.outputPermissions.length === 0
                    ? t('versions.none')
                    : selected.outputPermissions.map(labels.outputPermission).join(', ')}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                {t('versions.clientOverrideReason')}
              </dt>
              <dd className="mt-1">
                {policyValue(selected.clientOverrideReason).value}
              </dd>
            </div>
          </dl>
        </section>
      )}
    </section>
  )
}
